import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  SectionedMultiSelect,
  SectionedSection,
} from 'components/UI/Select/SectionedMultiSelect'
import { EmailContact } from 'models/emailIntegration'
import apiService from 'services/api'

export interface InboxFilters {
  from_emails?: string[]
  to_emails?: string[]
  date_greater?: string
  date_less?: string
  statuses?: string[]
  has_attachment?: string
  deal_assignment?: string
  deal_ids?: number[]
}

interface InboxFilterChipsProps {
  contactCompanyId: number
  filters: InboxFilters
  onChange: (filters: InboxFilters) => void
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Format a Date as YYYY-MM-DD. */
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

/** Compute start/end dates for a date preset key. */
function getDateRange(preset: string): { greater: string; less: string } {
  const end = new Date()
  const start = new Date()
  switch (preset) {
    case 'last_week':
      start.setDate(start.getDate() - 7)
      break
    case 'last_month':
      start.setMonth(start.getMonth() - 1)
      break
    case 'last_3_months':
      start.setMonth(start.getMonth() - 3)
      break
    case 'last_year':
      start.setFullYear(start.getFullYear() - 1)
      break
  }
  return { greater: formatDate(start), less: formatDate(end) }
}

/**
 * Build a display label from the selected values. Shows up to 2 labels
 * or "N selected" for more.
 */
function defaultFormatLabel(
  selected: string[],
  sections: SectionedSection<string>[],
  emptyLabel = 'All'
): string {
  if (selected.length === 0) return emptyLabel
  const labels = selected
    .map((val) => {
      for (const section of sections) {
        const item = section.items.find((i) => i.value === val)
        if (item) return item.label
      }
      return null
    })
    .filter(Boolean) as string[]
  if (labels.length <= 2) return labels.join(', ')
  return `${labels.length} selected`
}

/** Format a contact for display. */
function formatContactLabel(contact: EmailContact): string {
  return contact.name ? `${contact.name} (${contact.email})` : contact.email
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

/** Fetch distinct email contacts and return as section items. */
function useContactSections(
  type: 'senders' | 'recipients'
): SectionedSection<string>[] {
  const api = apiService()

  const { data } = useQuery({
    queryKey: ['email-contacts', type],
    queryFn: ({ signal }) => {
      const fetcher =
        type === 'senders'
          ? api.emailIntegration.getDistinctSenders
          : api.emailIntegration.getDistinctRecipients
      return fetcher({ search: '' }, signal)
    },
  })

  return useMemo(
    () => [
      {
        key: 'contacts',
        items: (data || []).map((c: EmailContact) => ({
          label: formatContactLabel(c),
          value: c.email,
        })),
      },
    ],
    [data]
  )
}

/** Build sections for the Deal filter (status + individual deals). */
function useDealFilterSections(
  contactCompanyId: number
): SectionedSection<string>[] {
  const api = apiService()

  const { data: deals } = useQuery({
    queryKey: ['deal-filter-list', contactCompanyId],
    queryFn: ({ signal }) =>
      api.emailIntegration.searchDeals(
        { contact_company_id: contactCompanyId },
        signal
      ),
  })

  return useMemo(() => {
    const statusSection: SectionedSection<string> = {
      key: 'status',
      items: [
        { label: 'Assigned', value: 'status:assigned' },
        { label: 'Unassigned', value: 'status:unassigned' },
      ],
    }
    const dealSection: SectionedSection<string> = {
      key: 'deals',
      items: (deals ?? []).map((d) => ({
        label: `${d.identifier} - ${d.deal_name}`,
        value: `deal:${d.id}`,
      })),
    }
    return [statusSection, dealSection]
  }, [deals])
}

// ---------------------------------------------------------------------------
// Deal filter encode/decode
// ---------------------------------------------------------------------------

/** Encode deal filter state as prefixed string values. */
function encodeDealSelection(filters: InboxFilters): string[] {
  const values: string[] = []
  if (filters.deal_assignment) values.push(`status:${filters.deal_assignment}`)
  if (filters.deal_ids?.length) {
    values.push(...filters.deal_ids.map((id) => `deal:${id}`))
  }
  return values
}

/** Decode deal selection back into filter state. */
function decodeDealSelection(
  values: string[],
  filters: InboxFilters
): InboxFilters {
  const next = { ...filters }
  const statusValues = values
    .filter((v) => v.startsWith('status:'))
    .map((v) => v.replace('status:', ''))
  const dealIds = values
    .filter((v) => v.startsWith('deal:'))
    .map((v) => Number(v.replace('deal:', '')))

  // Both statuses selected = no assignment filter (same as "All")
  const hasAssigned = statusValues.includes('assigned')
  const hasUnassigned = statusValues.includes('unassigned')
  if (hasAssigned && !hasUnassigned) {
    next.deal_assignment = 'assigned'
  } else if (hasUnassigned && !hasAssigned) {
    next.deal_assignment = 'unassigned'
  } else {
    delete next.deal_assignment
  }
  if (dealIds.length > 0) {
    next.deal_ids = dealIds
  } else {
    delete next.deal_ids
  }
  return next
}

// ---------------------------------------------------------------------------
// Static sections
// ---------------------------------------------------------------------------

const STATUS_SECTIONS: SectionedSection<string>[] = [
  {
    key: 'status',
    items: [
      { label: 'Replied', value: 'replied' },
      { label: 'Not Replied', value: 'not_replied' },
    ],
  },
]

const ATTACHMENT_SECTIONS: SectionedSection<string>[] = [
  {
    key: 'attachment',
    items: [
      { label: 'Has Attachment', value: 'true' },
      { label: 'No Attachment', value: 'false' },
    ],
  },
]

const DATE_SECTIONS: SectionedSection<string>[] = [
  {
    key: 'date',
    items: [
      { label: 'Any time', value: 'any' },
      { label: 'Last week', value: 'last_week' },
      { label: 'Last month', value: 'last_month' },
      { label: 'Last 3 months', value: 'last_3_months' },
      { label: 'Last year', value: 'last_year' },
    ],
  },
]

// ---------------------------------------------------------------------------
// Date filter encode/decode
// ---------------------------------------------------------------------------

/** Encode date filter state to selection value. */
function encodeDateSelection(filters: InboxFilters): string[] {
  if (!filters.date_greater && !filters.date_less) return []
  for (const item of DATE_SECTIONS[0].items) {
    if (item.value === 'any') continue
    const range = getDateRange(item.value)
    if (
      range.greater === filters.date_greater &&
      range.less === filters.date_less
    ) {
      return [item.value]
    }
  }
  return []
}

/** Decode date selection into filter state. */
function decodeDateSelection(
  values: string[],
  filters: InboxFilters
): InboxFilters {
  const next = { ...filters }
  // "Any time" or empty = clear date filters
  const preset = values.filter((v) => v !== 'any').pop()
  if (preset) {
    const range = getDateRange(preset)
    next.date_greater = range.greater
    next.date_less = range.less
  } else {
    delete next.date_greater
    delete next.date_less
  }
  return next
}

/** Format the date filter label. */
function formatDateLabel(selected: string[]): string {
  const preset = selected.filter((v) => v !== 'any').pop()
  if (!preset) return 'Any time'
  const item = DATE_SECTIONS[0].items.find((i) => i.value === preset)
  return item?.label ?? 'Any time'
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Row of filter dropdowns for Deal, From, To, Status, Attachments, and Date. */
export function InboxFilterChips({
  contactCompanyId,
  filters,
  onChange,
}: InboxFilterChipsProps) {
  const senderSections = useContactSections('senders')
  const recipientSections = useContactSections('recipients')
  const dealSections = useDealFilterSections(contactCompanyId)

  // Deal
  const dealSelected = useMemo(() => encodeDealSelection(filters), [filters])
  const handleDealApply = useCallback(
    (selected: string[]) => onChange(decodeDealSelection(selected, filters)),
    [filters, onChange]
  )

  /** Create an apply handler that sets or clears an array filter key. */
  const makeArrayApply = useCallback(
    (key: 'from_emails' | 'to_emails' | 'statuses') => (selected: string[]) => {
      const next = { ...filters }
      if (selected.length > 0) {
        next[key] = selected
      } else {
        delete next[key]
      }
      onChange(next)
    },
    [filters, onChange]
  )

  const handleFromApply = useMemo(
    () => makeArrayApply('from_emails'),
    [makeArrayApply]
  )
  const handleToApply = useMemo(
    () => makeArrayApply('to_emails'),
    [makeArrayApply]
  )
  const handleStatusApply = useMemo(
    () => makeArrayApply('statuses'),
    [makeArrayApply]
  )

  // Attachments
  const handleAttachmentApply = useCallback(
    (selected: string[]) => {
      const next = { ...filters }
      // If both selected or none selected → clear filter
      if (selected.length === 1) {
        next.has_attachment = selected[0]
      } else {
        delete next.has_attachment
      }
      onChange(next)
    },
    [filters, onChange]
  )

  // Date
  const dateSelected = useMemo(() => encodeDateSelection(filters), [filters])
  const handleDateApply = useCallback(
    (selected: string[]) => onChange(decodeDateSelection(selected, filters)),
    [filters, onChange]
  )

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <SectionedMultiSelect
        buttonLabel="To"
        emptyLabel="To"
        sections={recipientSections}
        selected={filters.to_emails ?? []}
        onApply={handleToApply}
        searchPlaceholder="Search recipients..."
        formatLabel={defaultFormatLabel}
      />
      <SectionedMultiSelect
        buttonLabel="From"
        emptyLabel="From"
        sections={senderSections}
        selected={filters.from_emails ?? []}
        onApply={handleFromApply}
        searchPlaceholder="Search senders..."
        formatLabel={defaultFormatLabel}
      />
      <SectionedMultiSelect
        buttonLabel="Date"
        emptyLabel="Any time"
        sections={DATE_SECTIONS}
        selected={dateSelected}
        onApply={handleDateApply}
        searchable={false}
        formatLabel={(sel) => formatDateLabel(sel)}
      />
      <SectionedMultiSelect
        buttonLabel="Deal"
        emptyLabel="Deal"
        sections={dealSections}
        selected={dealSelected}
        onApply={handleDealApply}
        searchPlaceholder="Deal ID or name"
        formatLabel={defaultFormatLabel}
      />
      <SectionedMultiSelect
        buttonLabel="Status"
        emptyLabel="Status"
        sections={STATUS_SECTIONS}
        selected={filters.statuses ?? []}
        onApply={handleStatusApply}
        searchable={false}
        formatLabel={defaultFormatLabel}
      />
      <SectionedMultiSelect
        buttonLabel="Attachment"
        emptyLabel="Has attachment"
        sections={ATTACHMENT_SECTIONS}
        selected={filters.has_attachment ? [filters.has_attachment] : []}
        onApply={handleAttachmentApply}
        searchable={false}
        formatLabel={defaultFormatLabel}
      />
    </div>
  )
}
